/**
 * Dedicated category page — URL: /categorie/:slug
 *
 * Generic dynamic route: `:slug` is the value of `product.category` converted
 * to lowercase with hyphens (e.g. `racket_junior` -> `racket-junior`).
 * No new deployment needed to add a new category — tag any active product
 * with the new value and the route works.
 *
 * Behaviour :
 *  - Fetch all products, filter by category === slugToCategory(slug), active only.
 *  - If no product matches, render an empty state cleanly (no 404).
 *  - Reuses the existing ProductCard grid layout for visual consistency.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useLang, useSettings } from "../lib/providers";
import { goBack } from "../lib/navHelpers";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppFloater from "../components/WhatsAppFloater";
import ProductCard from "../components/ProductCard";
import { ArrowLeft } from "lucide-react";

// Route slug ↔ raw category value.
// Categories in DB are already lowercase snake_case (racket, racket_junior, string, ...).
// Slug rule: lowercase + underscores → hyphens.
export const categoryToSlug = (cat) => String(cat || "").toLowerCase().replace(/_/g, "-");
export const slugToCategory = (slug) => String(slug || "").toLowerCase().replace(/-/g, "_");

export default function CategoryPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [reviewStats, setReviewStats] = useState({});
  const [loading, setLoading] = useState(true);

  const category = useMemo(() => slugToCategory(slug), [slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prodRes, statsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/reviews/public/products-stats`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setProducts(prodRes.data || []);
        setReviewStats(statsRes.data || {});
      } catch (e) {
        console.error("CategoryPage load error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(
    () => products.filter((p) => p.active !== false && p.category === category).sort((a, b) => (a.stock_status === "out_of_stock" ? 1 : 0) - (b.stock_status === "out_of_stock" ? 1 : 0)),
    [products, category]
  );

  // Human label: prefer admin labels from settings, fallback to raw category value
  const catLabels = lang === "ar"
    ? (settings.product_category_labels_ar || {})
    : (settings.product_category_labels_fr || {});
  const label = catLabels[category] || category;

  useEffect(() => {
    document.title = `${label} — ${settings.site_name || "DZ Tennis Shopper"}`;
  }, [label, settings.site_name]);

  return (
    <div className="min-h-screen bg-[var(--c-bg)] text-white flex flex-col" data-testid="category-page">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <button
            onClick={() => goBack(navigate)}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-neon mb-6"
            data-testid="category-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === "ar" ? "رجوع" : "Retour"}
          </button>

          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mb-2"
            data-testid="category-title"
          >
            {label}
          </h1>
          <p className="text-sm text-white/50 uppercase tracking-widest mb-8" data-testid="category-count">
            {loading
              ? (lang === "ar" ? "جاري التحميل…" : "Chargement…")
              : `${filtered.length} ${lang === "ar" ? "منتج" : "produits"}`}
          </p>

          {!loading && filtered.length === 0 && (
            <div className="border border-white/10 bg-black/30 p-12 text-center" data-testid="category-empty">
              <p className="text-white/60 text-sm mb-4">
                {lang === "ar"
                  ? "لا توجد منتجات في هذه الفئة."
                  : "Aucun produit dans cette catégorie."}
              </p>
              <Link
                to="/"
                className="inline-block bg-neon text-black px-5 py-2 text-xs font-black uppercase tracking-widest"
              >
                {lang === "ar" ? "استكشف الكتالوج" : "Voir le catalogue"}
              </Link>
            </div>
          )}

          {filtered.length > 0 && (
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
              data-testid="category-grid"
            >
              {filtered.map((p, i) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  priority={i < 4}
                  reviewStats={reviewStats[p.id]}
                  onClick={() => navigate(`/produit/${p.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppFloater />
    </div>
  );
}
